import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useOkapiKy, useCallout } from '@folio/stripes/core';
import { Loading } from '@folio/stripes/components';
import { TitleManager } from '@folio/stripes/core';

import EmailSettingsForm from './EmailSettingsForm';

const EmailSettings = ({ stripes }) => {
  const intl = useIntl();
  const ky = useOkapiKy();
  const callout = useCallout();
  const queryClient = useQueryClient();

  // Fetch SMTP configuration
  const {
    data: smtpConfig,
    isLoading,
    error,
  } = useQuery(
    'smtp-configuration',
    async () => {
      try {
        const response = await ky.get('smtp-configuration?limit=1').json();
        return response.smtpConfigurations?.[0] || {};
      } catch (err) {
        console.warn('SMTP configuration API not available:', err);
        // Return empty config if API not available
        return {};
      }
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      // Don't treat as error if API not available
      onError: (err) => {
        console.warn('Failed to fetch SMTP config:', err);
      },
    },
  );

  // Save/Update SMTP configuration
  const saveMutation = useMutation(
    async (data) => {
      if (smtpConfig?.id) {
        // Update existing
        return ky
          .put(`smtp-configuration/${smtpConfig.id}`, { json: data })
          .json();
      } else {
        // Create new
        return ky.post('smtp-configuration', { json: data }).json();
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('smtp-configuration');
        callout.sendCallout({
          type: 'success',
          message: intl.formatMessage({
            id: 'ui-tenant-settings.settings.emailSettings.callout.updatedSettings.success',
          }),
        });
      },
      onError: (saveError) => {
        callout.sendCallout({
          type: 'error',
          message: intl.formatMessage({
            id: 'ui-tenant-settings.settings.emailSettings.callout.error',
          }),
        });
        console.error('Save error:', saveError);
      },
    },
  );

  // Test email functionality
  const testEmailMutation = useMutation(
    async (testEmail) => {
      return ky
        .post('email', {
          json: {
            notificationId: Date.now(),
            to: testEmail,
            header: intl.formatMessage({ id: 'ui-tenant-settings.settings.emailSettings.testEmail.subject' }),
            body: intl.formatMessage({ id: 'ui-tenant-settings.settings.emailSettings.testEmail.body' }),
            outputFormat: 'text/plain',
          },
          headers: {
            'Accept': 'text/plain',
          },
        })
        .text();
    },
    {
      onSuccess: () => {
        callout.sendCallout({
          type: 'success',
          message: intl.formatMessage({
            id: 'ui-tenant-settings.settings.emailSettings.testEmail.success',
          }),
        });
      },
      onError: (testError) => {
        callout.sendCallout({
          type: 'error',
          message: intl.formatMessage({
            id: 'ui-tenant-settings.settings.emailSettings.testEmail.error',
          }),
        });
        console.error('Test email error:', testError);
      },
    },
  );

  const handleSave = (formData) => {
    saveMutation.mutate(formData);
  };

  const handleTestEmail = (testEmail) => {
    testEmailMutation.mutate(testEmail);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    // <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <TitleManager
      page={intl.formatMessage({
        id: 'ui-tenant-settings.settings.emailSettings.title',
      })}
    >
      <EmailSettingsForm
        initialValues={smtpConfig || {}}
        onSubmit={handleSave}
        onTestEmail={handleTestEmail}
        isLoading={saveMutation.isLoading}
        isTestLoading={testEmailMutation.isLoading}
        stripes={stripes}
        hasError={!!error}
      />
    </TitleManager>
    // </div>
  );
};

EmailSettings.propTypes = {
  stripes: PropTypes.shape({
    hasPerm: PropTypes.func,
  }),
};

export default EmailSettings;
